import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {message} from 'antd';
import {recycleService} from '@/services/recycleService';
import type {
  CancelOrderCommand,
  ConfirmFinalQuoteCommand,
  RecycleOrder,
  RecycleOrderQuery,
  SettleOrderCommand
} from '@/types/recycle';
import {RecycleOrderStatus} from '@/types/recycle';

// 异步操作
export const fetchOrders = createAsyncThunk(
  'recycleOrder/fetchOrders',
  async (query: RecycleOrderQuery) => {
    const response = await recycleService.getOrders(query);
    return response;
  }
);

export const fetchOrderByNo = createAsyncThunk(
  'recycleOrder/fetchOrderByNo',
  async (orderNo: string) => {
    console.log('fetchOrderByNo called with orderNo:', orderNo);
    const response = await recycleService.getOrderByNo(orderNo);
    console.log('fetchOrderByNo response:', response);
    return response;
  }
);

export const fetchOrderById = createAsyncThunk(
  'recycleOrder/fetchOrderById',
  async (orderId: number) => {
    console.log('fetchOrderById called with orderId:', orderId);
    const response = await recycleService.getOrderById(orderId);
    console.log('fetchOrderById response:', response);
    return response;
  }
);

export const reviewOrder = createAsyncThunk(
  'recycleOrder/reviewOrder',
  async (data: { orderNo: string }) => {
    const response = await recycleService.reviewOrder(data);
    return { ...data, success: response };
  }
);

export const updateOrderStatus = createAsyncThunk(
  'recycleOrder/updateOrderStatus',
  async ({ orderNo, status }: { orderNo: string; status: string }) => {
    const response = await recycleService.updateOrderStatus(orderNo, status);
    return response;
  }
);

export const orderSendOut = createAsyncThunk(
  'recycleOrder/orderSendOut',
  async ({ orderNo, data }: {
    orderNo: string;
    data: {
      sendOutMethod: string;
      visitAddress?: string;
      visitProvince?: string;
      visitCity?: string;
      visitCounty?: string;
      visitContactName?: string;
      visitContactPhone?: string;
      visitTime?: string;
      postageSettlementMethod?: string;
      remark?: string
    }
  }) => {
    const response = await recycleService.orderSendOut(orderNo, data);
    return response;
  }
);

export const settleOrder = createAsyncThunk(
  'recycleOrder/settleOrder',
  async ({ orderNo, data }: { orderNo: string; data: SettleOrderCommand }) => {
    const response = await recycleService.settleOrder(orderNo, data);
    return response;
  }
);

export const cancelOrder = createAsyncThunk(
  'recycleOrder/cancelOrder',
  async ({ orderNo, data }: { orderNo: string; data: CancelOrderCommand }) => {
    const response = await recycleService.cancelOrder(orderNo, data);
    return response;
  }
);

export const confirmFinalQuote = createAsyncThunk(
  'recycleOrder/confirmFinalQuote',
  async ({ orderNo, data }: { orderNo: string; data: ConfirmFinalQuoteCommand }) => {
    const response = await recycleService.confirmFinalQuote(orderNo, data);
    return response;
  }
);

export const exportOrders = createAsyncThunk(
  'recycleOrder/exportOrders',
  async (query: RecycleOrderQuery) => {
    const response = await recycleService.exportOrders(query);
    return response;
  }
);

// 状态类型
interface RecycleOrderState {
  list: RecycleOrder[];
  currentOrder: RecycleOrder | null;
  total: number;
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: RecycleOrderState = {
  list: [],
  currentOrder: null,
  total: 0,
  loading: false,
  error: null,
};

// Slice
const recycleOrderSlice = createSlice({
  name: 'recycleOrder',
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchOrders
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取订单列表失败';
        message.error(state.error);
      });

    // fetchOrderByNo
    builder
      .addCase(fetchOrderByNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderByNo.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderByNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取订单详情失败';
        message.error(state.error);
      });

    // fetchOrderById
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取订单详情失败';
        message.error(state.error);
      });

    // reviewOrder
    builder
      .addCase(reviewOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewOrder.fulfilled, (state, action) => {
        state.loading = false;
        // 审核成功后，状态会从 PENDING_REVIEW 变为 PENDING_SENDOUT
        // 同时后端会生成顺丰运单号等信息，所以需要重新获取数据
        const index = state.list.findIndex(item => item.orderNo === action.payload.orderNo);
        if (index !== -1) {
          // 临时更新状态，等待列表刷新获取完整数据
          state.list[index].status = RecycleOrderStatus.PENDING_SENDOUT;
        }
        if (state.currentOrder?.orderNo === action.payload.orderNo) {
          // 临时更新状态，等待详情刷新获取完整数据
          state.currentOrder.status = RecycleOrderStatus.PENDING_SENDOUT;
        }
        message.success('审核订单成功');
      })
      .addCase(reviewOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '审核订单失败';
        message.error(state.error);
      });

    // updateOrderStatus
    builder
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.orderNo === action.payload.orderNo);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder?.orderNo === action.payload.orderNo) {
          state.currentOrder = action.payload;
        }
        message.success('更新订单状态成功');
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新订单状态失败';
        message.error(state.error);
      });

    // orderSendOut
    builder
      .addCase(orderSendOut.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(orderSendOut.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.orderNo === action.payload.orderNo);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder?.orderNo === action.payload.orderNo) {
          state.currentOrder = action.payload;
        }
        message.success('订单发货成功');
      })
      .addCase(orderSendOut.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '订单发货失败';
        message.error(state.error);
      });

    // settleOrder
    builder
      .addCase(settleOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(settleOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.orderNo === action.payload.orderNo);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder?.orderNo === action.payload.orderNo) {
          state.currentOrder = action.payload;
        }
        message.success('订单结算成功');
      })
      .addCase(settleOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '订单结算失败';
        message.error(state.error);
      });

    // cancelOrder
    builder
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.orderNo === action.payload.orderNo);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder?.orderNo === action.payload.orderNo) {
          state.currentOrder = action.payload;
        }
        message.success('订单取消成功');
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '订单取消失败';
        message.error(state.error);
      });

    // confirmFinalQuote
    builder
      .addCase(confirmFinalQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmFinalQuote.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.orderNo === action.payload.orderNo);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder?.orderNo === action.payload.orderNo) {
          state.currentOrder = action.payload;
        }
        const actionType = action.payload.status === RecycleOrderStatus.PENDING_SETTLEMENT ? '接受' : '拒绝';
        message.success(`最终报价${actionType}成功`);
      })
      .addCase(confirmFinalQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '确认最终报价失败';
        message.error(state.error);
      });

    // exportOrders
    builder
      .addCase(exportOrders.pending, (state) => {
        state.error = null;
      })
      .addCase(exportOrders.fulfilled, (state, action) => {
        // 创建下载链接
        const url = window.URL.createObjectURL(action.payload);
        const link = document.createElement('a');
        link.href = url;
        link.download = `回收订单_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('订单导出成功');
      })
      .addCase(exportOrders.rejected, (state, action) => {
        state.error = action.error.message || '订单导出失败';
        message.error(state.error);
      });
  },
});

export const { clearCurrentOrder, clearError } = recycleOrderSlice.actions;
export default recycleOrderSlice.reducer;
